import React, { useEffect, useState } from 'react';
import { MonthHistoryDay } from '../types';

interface MonthLineChartProps {
    history: MonthHistoryDay[];
    animate: boolean;
}

const MonthLineChart: React.FC<MonthLineChartProps> = ({ history, animate }) => {
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
    const [pointsVisible, setPointsVisible] = useState<boolean>(false);

    const width = 800;
    const height = 200;
    const padding = 40;
    const topPadding = 60;

    // Dispara animação sempre que os dados mudarem (mesma abordagem do HistoryYearChart)
    useEffect(() => {
        if (history.length === 0) return;

        // Reseta visibilidade dos pontos
        setPointsVisible(false);

        if (!animate) {
            // Sem animação: mostra tudo imediatamente
            const path = document.querySelector('.dsb-estx-mes-line-path') as SVGPathElement;
            if (path) {
                path.style.strokeDasharray = '';
                path.style.strokeDashoffset = '';
                path.style.transition = 'none';
            }
            setPointsVisible(true);
            return;
        }

        // Com animação: igual ao HistoryYearChart
        setTimeout(() => {
            const path = document.querySelector('.dsb-estx-mes-line-path') as SVGPathElement;
            if (path) {
                const length = path.getTotalLength();
                path.style.strokeDasharray = `${length}`;
                path.style.strokeDashoffset = `${length}`;

                // Força reflow
                path.getBoundingClientRect();

                path.style.transition = 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)';
                path.style.strokeDashoffset = '0';
            }

            // Pontos aparecem depois de um delay
            setTimeout(() => {
                setPointsVisible(true);
            }, 400);
        }, 100);
    }, [history, animate]);

    if (history.length === 0) return null;

    const maxCards = Math.max(...history.map(d => d.cardsStudied), 1);
    const minDay = Math.min(...history.map(d => d.day));
    const maxDay = Math.max(...history.map(d => d.day));
    const dayRange = maxDay - minDay || 1;
    const usableWidth = width - 2 * padding;

    const points = history.map(day => {
        const x = padding + ((day.day - minDay) / dayRange) * usableWidth;
        const y = topPadding + (height - 2 * padding) - (day.cardsStudied / maxCards) * (height - 2 * padding);
        return { x, y, day: day.day, cards: day.cardsStudied };
    });

    const pathData = points.reduce((path, point, index) => {
        return path + (index === 0 ? `M ${point.x} ${point.y}` : ` L ${point.x} ${point.y}`);
    }, '');

    // Grid com valores arredondados (igual ao HistoryYearChart)
    const calculateGridValues = (max: number): number[] => {
        if (max <= 10) {
            const step = 2;
            const roundedMax = Math.ceil(max / step) * step;
            return [0, step, step * 2, step * 3, step * 4, step * 5].filter(v => v <= roundedMax + step);
        }
        let step = 50;
        if (max > 500) step = 100;
        if (max > 1000) step = 200;
        if (max > 2000) step = 500;
        const roundedMax = Math.ceil(max / step) * step;
        const values: number[] = [];
        for (let i = 0; i <= roundedMax; i += step) {
            values.push(i);
        }
        return values;
    };

    const gridValues = calculateGridValues(maxCards);
    const gridMax = gridValues[gridValues.length - 1] || maxCards;

    return (
        <div className="dsb-estx-mes-line-chart">
            <h3>Evolução do Mês</h3>
            <svg
                className="dsb-estx-mes-line-svg"
                viewBox={`0 0 ${width} ${height + topPadding}`}
                style={{ overflow: 'visible' }}
            >
                {/* Grid horizontal com valores arredondados */}
                {gridValues.map((value, i) => {
                    const y = topPadding + (height - 2 * padding) - (value / gridMax) * (height - 2 * padding);
                    return (
                        <g key={i}>
                            <line
                                className="dsb-estx-mes-line-grid"
                                x1={padding} y1={y}
                                x2={width - padding} y2={y}
                            />
                            <text
                                className="dsb-estx-mes-line-label"
                                x={padding - 10} y={y + 4}
                                textAnchor="end"
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}

                {/* Linha animada */}
                {points.length > 1 && (
                    <path
                        className="dsb-estx-mes-line-path"
                        d={pathData}
                    />
                )}

                {/* Pontos */}
                {points.map((point, i) => (
                    <circle
                        key={i}
                        className="dsb-estx-mes-line-point"
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        style={{
                            opacity: pointsVisible ? 1 : 0,
                            transform: pointsVisible ? 'scale(1)' : 'scale(0)',
                            transition: `opacity 0.3s ease ${i * 0.05}s, transform 0.3s ease ${i * 0.05}s`
                        }}
                        onMouseEnter={() => setTooltip({
                            x: point.x,
                            y: point.y - 20,
                            text: `Dia ${point.day}: ${point.cards} cards`
                        })}
                        onMouseLeave={() => setTooltip(null)}
                    />
                ))}

                {/* Tooltip */}
                {tooltip && (
                    <foreignObject
                        x={tooltip.x - 40} y={tooltip.y - 32}
                        width="110" height="28"
                    >
                        <div style={{
                            background: 'var(--btn-primary)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '5px',
                            fontSize: '11px',
                            fontWeight: 600,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 6px var(--platform-a25)',
                            pointerEvents: 'none',
                            lineHeight: 1.2
                        }}>
                            {tooltip.text}
                        </div>
                    </foreignObject>
                )}

                {/* Labels dos dias */}
                {points.map((point, i) => {
                    const shouldShow = points.length === 1 || i % Math.ceil(points.length / 8) === 0;
                    if (!shouldShow) return null;
                    return (
                        <text
                            key={i}
                            className="dsb-estx-mes-line-label"
                            x={point.x}
                            y={topPadding + height - padding + 20}
                            textAnchor="middle"
                        >
                            {point.day}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
};

export default MonthLineChart;